import { useHistory } from "react-router-dom";

import { api } from "@/APIRequests";
import { AssessmentEnum } from "@/core/constants";
import type { Profile } from "@/core/types";

import CompleteProfileWidget from "../CompleteProfileWidget/CompleteProfileWidget";
import {
  isACPSatisfactionSurveyTaskShown,
  isChangePasswordTaskShown,
  isCompleteScreeningTaskShown,
  isSNSETaskShown,
} from "../../../helpers/task-visibility";
import { isProfileCompleted } from "../helpers/profile-completion";
import { shouldUpdateInsurance } from "../../../helpers/insurance";

type TaskListProps = {
  profile: Profile;
  setShowNPSSurvey: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useTaskList = ({ profile, setShowNPSSurvey }: TaskListProps) => {
  const history = useHistory();

  if (!profile) {
    return { taskCount: 0, tasks: [] };
  }

  const allTasks = [
    {
      action: async () => {
        const data = {
          allow_multiple: false,
          assessment_type: AssessmentEnum.A5_V2_ASSESSMENT_TYPE,
          is_assessment_required: false,
        };

        // @ts-expect-error we need a typed api method
        await api.patient.assign_assessment({ data });
        history.push(`/app/assessment?assessmentType=${AssessmentEnum.A5_V2_ASSESSMENT_TYPE}`);
      },
      name: "Complete Screening",
      shouldShow: isCompleteScreeningTaskShown(profile),
    },
    {
      action: () => {
        history.push("/app/sns-e");
      },
      name: "Complete Social Needs Screening",
      shouldShow: isSNSETaskShown(profile),
    },
    {
      action: () => {
        history.push("/app/acp-satisfaction");
      },
      name: "Complete ACP Satisfaction Survey",
      shouldShow: isACPSatisfactionSurveyTaskShown(profile),
    },
    {
      action: () => {
        history.push("/app/change-password");
      },
      icon: "./icons/common/task/change-password-task.svg",
      name: "Change Password",
      shouldShow: isChangePasswordTaskShown(profile),
    },
    {
      action: () => {
        history.push("/app/patient-insurance");
      },
      icon: "./icons/common/task/Insurance.svg",
      name: "Update Insurance",
      shouldShow: shouldUpdateInsurance(profile),
    },
    {
      action: () => {
        history.push("/app/satisfaction-survey?mode=bhs");
      },
      name: "Complete Therapist Survey",
      shouldShow:
        profile.initial_bhs_satisfaction_survey_status !== "COMPLETE" &&
        profile.initial_video_consult,
    },
    {
      action: () => {
        history.push("/app/satisfaction-survey?mode=prescriber");
      },
      name: "Complete Prescriber Survey",
      shouldShow:
        profile.initial_prescriber_satisfaction_survey_status !== "COMPLETE" &&
        profile.initial_prescriber_call,
    },
    {
      action: () => setShowNPSSurvey((prev) => !prev),
      name: "Complete Survey",
      shouldShow: profile.nps_survey_enabled,
    },
    {
      customElement: <CompleteProfileWidget profile={profile} />,
      shouldShow: !isProfileCompleted(profile),
    },
  ];

  const tasks = allTasks.filter((task) => task.shouldShow);

  return { taskCount: tasks.length, tasks };
};
